import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CheckCircle2, RefreshCw, UploadCloud } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type JoliscreenImageDTO } from "../../api";
import { JOLISCREEN_VARIANT } from "../../components/badges";
import { EnumBadge, formatDate, objectUrl, Thumbnail } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Images published to Joliscreen.
 *
 * Rows arrive here from the artwork grid's "Add to Joliscreen" action; this
 * screen only decides whether each one is live (activate/suspend), removes it,
 * or re-pushes the feed and the S3 copies.
 */
export function JoliscreenPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.JOLISCREEN_IMAGES);
  const title = t("navigation.item.joliscreen", "Joliscreen");
  const [selected, setSelected] = useState<JoliscreenImageDTO[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["joliscreen"],
    queryFn: () => api.joliscreen.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["joliscreen"] });

  const cols = useMemo(() => {
    const c = defineColumns<JoliscreenImageDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => <Thumbnail url={objectUrl(row.image)} alt={row.title ?? ""} />,
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 56 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={JOLISCREEN_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 2 },
      ),
      c.text("title", (row) => row.title, { headerKey: "column.title", priority: 1 }),
      c.text("artist", (row) => row.artist, { headerKey: "column.artist", priority: 2 }),
      c.custom(
        "dimensions",
        (row) => row.width,
        (row) => `${row.width} x ${row.height}`,
        { headerKey: "column.dimensions", filter: "none", enableSorting: false, priority: 4 },
      ),
      c.text("tags", (row) => (row.tags ?? []).join(", "), {
        headerKey: "column.tags",
        enableSorting: false,
        priority: 5,
      }),
      c.text("created_at", (row) => formatDate(row.created_at), {
        headerKey: "column.created_at",
        priority: 4,
      }),
    ];
  }, []);

  const run = async (
    action: () => Promise<void>,
    successKey: string,
    successFallback: string,
    errorKey: string,
    errorFallback: string,
  ) => {
    try {
      await action();
      toast.success(t(successKey, successFallback));
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t(errorKey, errorFallback));
    }
  };

  const ids = () => selected.map((row) => row.id);

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<JoliscreenImageDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        onSelectionChange={setSelected}
        actions={[
          {
            label: t("menu.item.activate", "Activate"),
            icon: <CheckCircle2 className="size-4" />,
            variant: "success",
            write: true,
            requiresSelection: true,
            onClick: () =>
              void run(
                () => api.joliscreen.activate(ids()),
                "notification.joliscreen.activate.success",
                "Joliscreen image activated",
                "notification.joliscreen.activate.error",
                "Failed to activate Joliscreen image",
              ),
          },
          {
            label: t("menu.item.suspend", "Suspend"),
            icon: <Ban className="size-4" />,
            write: true,
            requiresSelection: true,
            onClick: () =>
              void run(
                () => api.joliscreen.suspend(ids()),
                "notification.joliscreen.suspend.success",
                "Image suspended successfully",
                "notification.joliscreen.suspend.error",
                "Failed to suspend image",
              ),
          },
          {
            label: t("menu.item.sync", "Sync"),
            icon: <RefreshCw className="size-4" />,
            write: true,
            onClick: async () => {
              const ok = await confirm({
                title: t("menu.item.sync", "Sync"),
                message: t("notification.joliscreen.sync.started", "Sync started"),
              });
              if (!ok) return;
              await run(
                () => api.joliscreen.sync(),
                "notification.joliscreen.sync.started",
                "Sync started",
                "notification.joliscreen.sync.error",
                "Failed to sync Joliscreen",
              );
            },
          },
          {
            label: t("menu.item.update.s3", "Update S3"),
            icon: <UploadCloud className="size-4" />,
            write: true,
            onClick: () =>
              void run(
                () => api.joliscreen.updateS3(),
                "notification.joliscreen.update.s3.started",
                "S3 update started",
                "notification.joliscreen.update.s3.error",
                "Failed to update S3",
              ),
          },
        ]}
        onDeleteSelected={async (selection) => {
          await api.joliscreen.removeBatch(selection.map((row) => row.id));
          toast.success(t("notification.delete.success", "Items deleted successfully"));
          await refresh();
        }}
        deleteSelectedConfirm={(count) => ({
          title: t("dialog.confirm.delete-images.title", "Delete images"),
          message: `${count} · ${t("dialog.confirm.delete.text", "Are you sure you want to delete the selected items?")}`,
        })}
      />
    </>
  );
}
